import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Spinner from "../../../components/Spinner/index.js";
import { db } from "../../../FirebaseConfig.ts";
import { collection, getDocs, query, where } from "firebase/firestore";
import { useSnapshotWhere } from "../../../hooks/useSnapshotWhere.js";
import Modal from "../../../components/Modal/index.js";
import ContentFormCompany from "../components/Sidebar/contentFormCompany.js";

function ListCompany({ user }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState([]);

  const { data: dataUser, isFetching: fetchingUser } = useSnapshotWhere(
    "users",
    {
      filter: {
        firstCondition: "id",
        secondCondition: "==",
        thirdCondition: user.uid,
      },
    }
  );

  const categoriesCollection = collection(db, "companies");

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    const fetchCompanies = async () => {
      if (dataUser && dataUser[0]) {
        try {
          setIsFetching(true);
          const q = query(
            categoriesCollection,
            where("majorCompanyId", "==", dataUser[0]?.majorCompanyId)
          );
          const querySnapshot = await getDocs(q);
          const companies = querySnapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
          }));
          setData(companies);
          setFilteredData(companies);
        } catch (error) {
          console.log(error);
        } finally {
          setIsFetching(false);
        }
      }
    };

    if (!fetchingUser) {
      fetchCompanies();
    }
  }, [fetchingUser, dataUser]);

  // Filter companies based on search term
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredData(data);
    } else {
      const filtered = data.filter(
        (company) =>
          company.name_company
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          company.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          company.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          company.lastName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredData(filtered);
    }
  }, [searchTerm, data]);

  const getCompanyInitials = (name) => {
    return name
      ?.split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getCompanyStats = () => {
    return {
      total: data.length,
      active: data.filter((company) => company.status !== "inactive").length,
      recent: data.filter((company) => {
        const createdAt =
          company.createdAt?.toDate?.() || new Date(company.createdAt);
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        return createdAt > thirtyDaysAgo;
      }).length,
    };
  };

  const stats = getCompanyStats();

  return (
    <>
      <Modal showModal={showModal} setShowModal={setShowModal}>
        <ContentFormCompany user={user} />
      </Modal>
      <div className="space-y-6">
        {/* Header Section */}
        <div
          className={`transition-all duration-700 ease-out ${
            isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="space-y-1">
              <h1 className="text-3xl font-bold text-gray-900 tracking-tight flex items-center gap-3">
                <div className="p-2 bg-[#49b0a8]/10 rounded-xl">
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="text-[#49b0a8]"
                  >
                    <path
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                Lista de Empresas
              </h1>
              <p className="text-gray-600">
                Gestiona y consulta todas las empresas registradas en el sistema
              </p>
            </div>

            <button
              className={`
              group relative overflow-hidden bg-[#49b0a8] hover:bg-[#3a8a84] text-white px-6 py-3 rounded-xl
              font-medium transition-all duration-300 ease-out transform hover:scale-105 hover:shadow-lg
              focus:outline-none focus:ring-4 focus:ring-blue-500/20 active:scale-95
              flex items-center gap-2 min-w-fit
            `}
              onClick={() => setShowModal(true)}
            >
              {/* Background animation */}
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>

              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                className="relative z-10"
              >
                <path
                  d="M12 5v14M5 12h14"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="relative z-10">Nueva Empresa</span>
            </button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-[#49b0a8]/10">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="text-[#49b0a8]"
                  >
                    <path
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Empresas</p>
                  <p className="text-xl font-bold text-gray-900">
                    {stats.total}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="text-green-600"
                  >
                    <path
                      d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Activas</p>
                  <p className="text-xl font-bold text-gray-900">
                    {stats.active}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="text-blue-600"
                  >
                    <path
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Recientes (30d)</p>
                  <p className="text-xl font-bold text-gray-900">
                    {stats.recent}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Search Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                <svg
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="search"
                className="block w-full pl-12 pr-4 py-4 text-sm text-gray-900 border border-gray-300 rounded-xl bg-gray-50 focus:ring-2 focus:ring-[#49b0a8] focus:border-[#49b0a8] transition-colors duration-200"
                placeholder="Buscar empresas por nombre, email o representante..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {searchTerm && (
              <div className="mt-3 flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  {filteredData.length} resultado
                  {filteredData.length !== 1 ? "s" : ""} encontrado
                  {filteredData.length !== 1 ? "s" : ""} para "{searchTerm}"
                </span>
                <button
                  onClick={() => setSearchTerm("")}
                  className="text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded-lg transition-colors duration-200"
                >
                  Limpiar
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Companies List */}
        <div
          className={`transition-all duration-700 ease-out delay-300 ${
            isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          {isFetching ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
              <div className="flex items-center justify-center py-12">
                <div className="text-center space-y-4">
                  <Spinner />
                  <p className="text-gray-600">Cargando empresas...</p>
                </div>
              </div>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
              <div className="text-center py-12 space-y-4">
                <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="text-gray-400"
                  >
                    <path
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {searchTerm
                      ? "No se encontraron empresas"
                      : "No hay empresas registradas"}
                  </h3>
                  <p className="text-gray-600">
                    {searchTerm
                      ? `No hay empresas que coincidan con "${searchTerm}"`
                      : "Aún no se han registrado empresas en el sistema"}
                  </p>
                </div>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="mt-4 bg-[#49b0a8] hover:bg-[#3a8a84] text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200"
                  >
                    Ver todas las empresas
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Desktop Table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        #
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Empresa
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Representante
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Contacto
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredData?.map((el, index) => (
                      <tr
                        key={el.id}
                        className={`
                        transition-all duration-200 hover:bg-gray-50 group
                        ${
                          isLoaded
                            ? "opacity-100 translate-y-0"
                            : "opacity-0 translate-y-4"
                        }
                      `}
                        style={{
                          animationDelay: `${index * 100}ms`,
                          animation: isLoaded
                            ? "fadeInUp 0.6s ease-out forwards"
                            : "none",
                        }}
                        onMouseEnter={() => setHoveredRow(index)}
                        onMouseLeave={() => setHoveredRow(null)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full text-sm font-medium text-gray-600 group-hover:bg-[#49b0a8] group-hover:text-white transition-colors duration-200">
                            {index + 1}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#49b0a8] rounded-lg flex items-center justify-center text-white font-medium text-sm">
                              {getCompanyInitials(el.name_company)}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {el.name_company}
                              </div>
                              <div className="text-sm text-gray-500">
                                ID: {el.id.slice(-8)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {el.name} {el.lastName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {el.email}
                          </div>
                          {el.phone && (
                            <div className="text-sm text-gray-500">
                              {el.phone}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <div className="w-1.5 h-1.5 rounded-full bg-green-400"></div>
                            Activa
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <Link to={`/plataforma/compania/${el.id}`}>
                              <button className="inline-flex items-center gap-2 text-[#49b0a8] hover:text-[#3a8a84] font-medium text-sm transition-colors duration-200">
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <path
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                Ver Detalles
                              </button>
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-4 p-4">
                {filteredData?.map((el, index) => (
                  <div
                    key={el.id}
                    className={`
                    bg-white border border-gray-200 rounded-xl p-4 space-y-4 hover:shadow-md transition-all duration-300
                    ${
                      isLoaded
                        ? "opacity-100 translate-y-0"
                        : "opacity-0 translate-y-4"
                    }
                  `}
                    style={{
                      animationDelay: `${index * 100}ms`,
                      animation: isLoaded
                        ? "fadeInUp 0.6s ease-out forwards"
                        : "none",
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-[#49b0a8] rounded-lg flex items-center justify-center text-white font-medium">
                        {getCompanyInitials(el.name_company)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 truncate">
                          {el.name_company}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {el.name} {el.lastName}
                        </p>
                        <p className="text-sm text-gray-500">{el.email}</p>
                      </div>
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-400"></div>
                        Activa
                      </span>
                    </div>

                    <div className="flex gap-2">
                      <Link
                        to={`/plataforma/compania/${el.id}`}
                        className="flex-1"
                      >
                        <button className="w-full bg-[#49b0a8] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#3a8a84] transition-colors duration-200">
                          Ver Detalles
                        </button>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <style jsx>{`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </div>
    </>
  );
}

export default ListCompany;
